import CachedIcon from '@mui/icons-material/Cached';
import {
  Alert,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from '@mui/material';
import { useState } from 'react';
import { formatBalanceToAbel, SmartLink } from '../../../abelcommon';
import { CenteredTableCell } from '../../../abelcommon/components/CenteredTableCell';
import { ABELSCAN_BLOCK_URL } from '../../../api/abel';
import { loadBalancerAPI } from '../../../api/load-balancer';
import { format } from 'date-fns';
import { MountableLoadingOverlay } from '../../../abelcommon/components/LoadingOverlay';
import { useTranslation } from 'react-i18next';

export const MinedBlockPage = () => {
  const [requestId, setRequestId] = useState<string>(undefined);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [pageAndLastCreatedAtMap, setPageAndLastCreatedAtMap] = useState<Record<number, string>>({});
  const { t } = useTranslation();

  const dateFormat = 'yyyy-MM-dd HH:mm:ss';

  const {
    data: minedBlockInfos,
    isLoading: getMinedBlockInfosLoading,
    error: getMinedBlockInfosError,
    refetch: refetchMinedBlockInfos,
    isPreviousData,
  } = loadBalancerAPI.hookWrapperQuery.useGetMinedBlockInfos(
    rowsPerPage,
    pageAndLastCreatedAtMap[page],
    undefined,
    requestId
  );

  const handlePageChange = (nextPage: number) => {
    if (minedBlockInfos) {
      setPageAndLastCreatedAtMap((prevState) => ({
        ...prevState,
        ...(nextPage > page ? { [nextPage]: minedBlockInfos.data[minedBlockInfos.data.length - 1]?.createdAt } : {}),
      }));
    }
    setPage(nextPage);
  };

  const handleChangeRowsPerPage = (nextRowsPerPage: number) => {
    setPage(0);
    setRowsPerPage(nextRowsPerPage);
    setRequestId(new Date().getTime().toString());
  };

  return (
    <Card>
      <CardHeader
        title={t('minedBlockPage.minedList')}
        action={
          <IconButton aria-label="reload" onClick={() => refetchMinedBlockInfos()}>
            <CachedIcon />
          </IconButton>
        }
      />
      <CardContent sx={{ overflow: 'auto', px: 0, pt: 0, position: 'relative' }}>
        <TableContainer sx={{ minHeight: '320px', maxHeight: '600px', overflow: 'auto' }}>
          <Table aria-label="simple table" sx={{ minWidth: '500px' }} stickyHeader>
            <TableHead>
              <TableRow>
                <CenteredTableCell>{t('minedBlockPage.height')}</CenteredTableCell>
                <CenteredTableCell>{t('minedBlockPage.blockHash')}</CenteredTableCell>
                <CenteredTableCell>{t('minedBlockPage.abelReward')}</CenteredTableCell>
                <CenteredTableCell>{t('minedBlockPage.createdAt')}</CenteredTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {getMinedBlockInfosLoading ? (
                <TableRow>
                  <CenteredTableCell colSpan={4}>
                    <CircularProgress />
                  </CenteredTableCell>
                </TableRow>
              ) : getMinedBlockInfosError ? (
                <TableRow>
                  <CenteredTableCell colSpan={4}>
                    <Alert severity="error" sx={{ backgroundColor: 'transparent', p: 0, textAlign: 'center' }}>
                      {t('minedBlockPage.cannotGetMinedBlockInfo')}
                    </Alert>
                  </CenteredTableCell>
                </TableRow>
              ) : !minedBlockInfos || minedBlockInfos.data.length === 0 ? (
                <TableRow>
                  <CenteredTableCell colSpan={4}>{t('global.noData')}</CenteredTableCell>
                </TableRow>
              ) : (
                minedBlockInfos.data.map((row, index) => {
                  return (
                    <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <CenteredTableCell>
                        <SmartLink to={`${ABELSCAN_BLOCK_URL}/${row.height}`}>{row.height}</SmartLink>
                      </CenteredTableCell>
                      <CenteredTableCell>
                        <SmartLink to={`${ABELSCAN_BLOCK_URL}/${row.blockHash}`}>{row.blockHash}</SmartLink>
                      </CenteredTableCell>
                      <CenteredTableCell>{!row.reward ? '--' : formatBalanceToAbel(row.reward)}</CenteredTableCell>
                      <CenteredTableCell>
                        {!row.createdAt ? '--' : format(new Date(row.createdAt), dateFormat)}
                      </CenteredTableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          sx={{ pl: 0, mr: 2 }}
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={minedBlockInfos?.total ?? 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, page) => handlePageChange(page)}
          labelRowsPerPage={t('global.rowsPerPage')}
          onRowsPerPageChange={(e) => handleChangeRowsPerPage(+e.target.value)}
        />
        {isPreviousData && <MountableLoadingOverlay />}
      </CardContent>
    </Card>
  );
};
